import styled from "styled-components";

// Main container for the bio page
export const StyledBioPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #fff;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

// Hero Section
export const StyledHeroSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 20px;
  background: linear-gradient(135deg, #ebfbff 0%, #f5f9ff 100%);
  border-radius: 12px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    padding: 40px 15px;
  }
`;

export const StyledProfileImage = styled.div<{ imageUrl?: string }>`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background-color: #8dabe2;
  background-image: ${(props) =>
    props.imageUrl ? `url(${props.imageUrl})` : "none"};
  background-size: cover;
  background-position: center;
  border: 5px solid #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 4rem;
  font-weight: bold;

  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
    font-size: 3rem;
  }
`;

export const StyledProviderName = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin: 10px 0;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const StyledHeadline = styled.h2`
  font-size: 1.5rem;
  color: #666;
  font-weight: 400;
  margin: 10px 0;
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

export const StyledContactInfo = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
  flex-wrap: wrap;
  justify-content: center;

  a {
    color: #4a90e2;
    text-decoration: none;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 5px;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    gap: 15px;
  }
`;

// Section Containers
export const StyledSection = styled.section`
  margin-bottom: 60px;
  padding: 40px;
  background-color: #f9fafb;
  border-radius: 12px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
    padding: 25px 20px;
  }
`;

export const StyledSectionTitle = styled.h2`
  font-size: 2rem;
  color: #333;
  margin-bottom: 20px;
  font-weight: 700;
  border-bottom: 3px solid #8dabe2;
  padding-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const StyledBioText = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #555;
  white-space: pre-line;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

// Services Section
export const StyledServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 30px;
  margin-top: 30px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

export const StyledServiceCard = styled.div`
  background-color: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

export const StyledServiceTitle = styled.h3`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 10px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

export const StyledServiceDescription = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
`;

export const StyledServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 15px 0;

  li {
    padding: 8px 0;
    padding-left: 25px;
    position: relative;
    color: #555;

    &:before {
      content: "✓";
      position: absolute;
      left: 0;
      color: #4caf50;
      font-weight: bold;
      font-size: 1.2rem;
    }
  }
`;

export const StyledServicePricing = styled.div`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
  font-size: 1.1rem;
  color: #4a90e2;
  font-weight: 600;
`;

// Contact Form Section
export const StyledContactForm = styled.form`
  max-width: 700px;
  margin: 30px auto 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const StyledFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const StyledLabel = styled.label`
  font-size: 1rem;
  color: #333;
  font-weight: 500;
`;

export const StyledInput = styled.input`
  padding: 12px 15px;
  font-size: 1rem;
  border: 2px solid #ddd;
  border-radius: 6px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }

  &.error {
    border-color: #e74c3c;
  }
`;

export const StyledTextarea = styled.textarea`
  padding: 12px 15px;
  font-size: 1rem;
  border: 2px solid #ddd;
  border-radius: 6px;
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }

  &.error {
    border-color: #e74c3c;
  }
`;

export const StyledErrorMessage = styled.span`
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: -5px;
`;

// Required attribution shown in place of the (hidden) floating reCAPTCHA
// badge - see the note next to the <style> tag that hides it.
export const StyledRecaptchaNotice = styled.p`
  font-size: 0.8rem;
  color: #888;
  text-align: center;
  margin: 0;

  a {
    color: #888;
    text-decoration: underline;

    &:hover {
      color: #4a90e2;
    }
  }
`;

export const StyledSubmitButton = styled.button`
  padding: 15px 30px;
  font-size: 1.1rem;
  color: #fff;
  background-color: #4a90e2;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
  font-weight: 600;

  &:hover {
    background-color: #357abd;
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

export const StyledSuccessMessage = styled.div`
  padding: 15px;
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  text-align: center;
  font-size: 1rem;
`;

export const StyledErrorAlert = styled.div`
  padding: 15px;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  text-align: center;
  font-size: 1rem;
`;

// Loading & Error States
export const StyledLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
`;

export const StyledNotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: 40px;

  h1 {
    font-size: 3rem;
    color: #666;
    margin-bottom: 20px;
  }

  p {
    font-size: 1.2rem;
    color: #888;
    margin-bottom: 30px;
  }

  a {
    color: #4a90e2;
    text-decoration: none;
    font-size: 1.1rem;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// Social Links
export const StyledSocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
  justify-content: center;

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #4a90e2;
    color: #fff;
    text-decoration: none;
    font-size: 1.2rem;
    transition: background-color 0.2s, transform 0.2s;

    &:hover {
      background-color: #357abd;
      transform: scale(1.1);
    }
  }
`;
